import { useEffect, useState } from 'react';

import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { isSameDay } from 'date-fns';

import { useCareMatrix } from 'hooks/useCareMatrix';
import { getPatientMoodTracker } from 'services/user';

import { Error } from 'components/CareMatrixModule/components';

import { CalendarDay } from './calendar-day';
import { CalendarHeader } from './calendar-header';
import { DayDetails } from './day-details';

import { CalendarDayProps } from './types';

import * as S from './styles';

export function MoodTracker() {
  const { patientCognitoId } = useCareMatrix();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(undefined);

  const {
    data: daysWithMoodTracker,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['getPatientMoodTracker', patientCognitoId, currentMonth],
    queryFn: () => getPatientMoodTracker(patientCognitoId, currentMonth),
  });

  function handleMonthChange(newMonth: Date) {
    setCurrentMonth(newMonth);
  }

  function handleCloseDayDetails() {
    setSelectedDay(undefined);
  }

  function handleSelectDay(day: Date) {
    const isDayWithMoodTracker = daysWithMoodTracker?.find(eachDay =>
      isSameDay(eachDay.date, day),
    );

    if (isDayWithMoodTracker) {
      setSelectedDay(day);

      document.getElementById('assessment-title')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  useEffect(() => {
    setCurrentMonth(new Date());
    setSelectedDay(undefined);
  }, [patientCognitoId]);

  return (
    <Box>
      {!isError ? (
        <S.Wrapper
          pt={3}
          px={4}
          bgcolor="white"
          gap={3}
          display="flex"
          flexDirection="column"
        >
          {selectedDay && (
            <DayDetails date={selectedDay} onClose={handleCloseDayDetails} />
          )}
          <S.Calendar
            slots={{
              calendarHeader: CalendarHeader,
              day: CalendarDay,
            }}
            slotProps={{
              calendarHeader: {
                onMonthChange: handleMonthChange,
                currentMonth,
              },
              day: {
                daysWithMoodTracker,
                onDaySelect: handleSelectDay,
                selectedDay,
                isLoading,
              } as CalendarDayProps,
            }}
            value={currentMonth}
            onChange={newValue => setCurrentMonth(newValue)}
            defaultValue={new Date()}
            showDaysOutsideCurrentMonth={false}
          />
        </S.Wrapper>
      ) : (
        <Error />
      )}
    </Box>
  );
}
